import classNames from 'classnames';
import isFunction from 'lodash/isFunction';
import React, {
    useCallback, useEffect, useState, useMemo, useRef
} from 'react';
import { useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { isFeatureActive } from '@helpers';
import { Button } from 'palette/Button';
import { ProductCard, ProductCardContextProvider } from 'palette/ProductCard';
import { CARD_THEMES } from 'palette/ProductCard/constants';
import { useActiveDeal } from '@common/ActivateDeals/hooks';
import { useAncestorLogger } from '@common/Hooks/useAncestorLogger';
import { useConfig } from '@common/Hooks/useConfig';
import { useIntersectionObserver } from '@common/Hooks/useIntersectionObserver';
import { get } from '@common/Http';
import { FavoriteButton } from '@common/ProductCard/components/FavoriteButton';
import { getPriceStructure, parsePrices } from '@common/ProductCard/helpers/content/prices';
import { ReactOnRailsRootComponent } from '@common/ReactOnRails';
import { SP_EVENT_PRODUCT_IMPRESSION } from '@common/Tracking';
import { handleBmpProductViewImpressionTracking } from '@common/Tracking/BmpProductView/handlers';
import { setItemsParameters } from '@common/Tracking/itemsParameters';
import { setRealListValue } from '@common/Tracking/RealList';
import { pushDataToTracking } from '@common/Tracking/sponsoredProducts/pushDataToTracking';
import { isBoozt, isWebshopDesktop } from '@helpers/env';
import {
    CARD_TYPES_ATTRIBUTES,
    isProductCardCarouselEnabled,
    parseDataForProductCard
} from '../helpers';

const handleBmpTracking = (settings) => {
    if (settings) {
        handleBmpProductViewImpressionTracking(settings);
    }
};

const favoriteButton = ({
    favoritesTrackingValue,
    actionButtonsPosition,
    itemsParameters,
    interactionOrigin
}) => (
    <FavoriteButton
        iconSize={actionButtonsPosition === 'image' ? 'small' : 'medium'}
        interactionOrigin={interactionOrigin}
        isTransparent={actionButtonsPosition === 'float'}
        itemsParameters={itemsParameters}
        trackingValue={favoritesTrackingValue}
    />
);

const getActionsButtons = (buttons, position) => {
    if (buttons && !!buttons.length) {
        return buttons.map((button) => {
            const { settings, callback } = button;
            const { iconName, iconColor, theme } = settings;
            const iconSize = position === 'image' ? 'small' : 'medium';

            return (
                <Button
                    key={iconName}
                    iconColor={iconColor ?? 'white'}
                    iconName={iconName ?? 'palette_cart'}
                    size={iconSize}
                    theme={theme ?? 'primary'}
                    onClick={callback}
                />
            );
        });
    }

    return null;
};

function ProductCardRedesign({
    product,
    type = 'listingPage',
    imageSizes,
    isDisabled = false,
    availableImageDimensions = undefined,
    hasToShowFavoriteButton = false,
    hasToShowFavoriteButtonLast = false,
    lazyLoad = false,
    brandnameFieldTagname = 'span',
    className: classNameProp = '',
    trackingValues = {},
    cardStrings = {},
    isSelectable = false,
    isSelected = false,
    hasToTrackImpressions = false,
    withAnimation = false,
    actionButtons = [],
    topHeaderComponent = null,
    customDescriptionComponent = React.ReactNode,
    onClick = () => {},
    onLinkClick = () => {},
    onLinkContextMenu = () => {},
    onDisabledNotificationClick = () => {},
    onMouseDown = () => {},
    hasHoverEffect: hasHoverEffectProp = true,
    allowVideo = false,
    theme = CARD_THEMES.default,
    breadcrumbs = []
}) {
    const config = useConfig();
    const activatedDeals = useActiveDeal()?.activated;
    const isSponsoredProduct = !!product.sponsored_product_tracking;
    // for sponsored products have to render active deals related parts on client side only,
    // because there is no info about active deals on server side
    const [isActiveDealsOn, setIsActiveDealsOn] = useState(!isSponsoredProduct && activatedDeals);
    const [observerTriggered, setObserverTriggered] = useState(false);
    const targetRef = useRef(null);
    const [observer] = useIntersectionObserver(
        {
            active: isSponsoredProduct,
            elementRef: targetRef,
            threshold: 0.5
        }
    );

    const isMobile = useMediaQuery({ maxWidth: 767 });

    const {
        URLPrefix = App.Config.URLPrefix,
        colorSwatches = App.Config.colorSwatches,
        styleFlex = {}
    } = config;

    const isSponsoredTrackingFeatureActive = isFeatureActive('PLT-47472-send-impression-and-click-tracking-to-snow-plow', config);
    const isSponsoredClickUrlFeatureActive = isFeatureActive(
        'PLT-64769-some-clicks-not-coming-through-for-sponsored-products', config
    );
    const isImagesCarouselFeatureActive = isFeatureActive('PLT-62256-images-carousel-in-product-card', config);
    const shouldEnableHoverOnBothWebshops = isFeatureActive('PLT-27835-enable-hover-effect-on-cards', config)
        && isWebshopDesktop(config);
    const isModelImageSwitchEnabled = isFeatureActive('PLT-50092_toggle_switch_product_model_images', config);
    const isNewPriceStructureFeatureActive = isFeatureActive('PLT-67565_product-card-redesign-new-prices-structure', config);

    const { variants } = product ?? {};
    const { in_stock: isInStock } = variants?.[0] ?? {};

    const imagesParameters = useMemo(() => ({
        imageSizes,
        availableImageDimensions
    }), [availableImageDimensions, imageSizes]);

    const cardConfig = useMemo(() => ({
        ...(CARD_TYPES_ATTRIBUTES?.[type] ?? {}),
        URLPrefix,
        imagesParameters,
        cardStrings,
        numberOfImageSplashes: 1,
        colorSwatches,
        styleFlex
    }), [URLPrefix, cardStrings, colorSwatches, imagesParameters, type, styleFlex]);

    const productData = useMemo(() => parseDataForProductCard(
        product, cardConfig, breadcrumbs
    ), [cardConfig, product, breadcrumbs]);

    const isMissingMedia = productData?.data?.media === undefined;
    const isCarouselEnabled = isProductCardCarouselEnabled(isMobile, isImagesCarouselFeatureActive);

    useAncestorLogger(targetRef, 'front_end.product-card.missing-media', isMissingMedia);

    const productPrices = useMemo(() => parsePrices({
        variants,
        isActiveDealsOn,
        cardConfig
    }), [variants, isActiveDealsOn, cardConfig]);

    const productPricesStructure = useMemo(() => getPriceStructure({
        cardConfig,
        isNewPriceStructureFeatureActive,
        isActiveDealsOn,
        variants
    }), [cardConfig, isNewPriceStructureFeatureActive, isActiveDealsOn, variants]);

    const modelImageMode = useSelector(({ listing }) => listing.modelImageMode);

    const {
        favorites: favoritesTrackingValue,
        list: realListValue,
        bmp: bmpTrackingSettings,
        dataAttributes = {},
        itemsParameters = {},
        interactionOrigin,
        sponsored: sponsoredTrackingValues = {}
    } = trackingValues;

    const {
        click_url: sponsoredCardClickUrl = '', impression_url: sponsoredCardImpressionsUrl = '', ad_id: adId = null
    } = product.sponsored_product_tracking || {};

    const handlePushToTracking = useCallback((event) => {
        pushDataToTracking({
            id: product.id,
            variants: product.variants,
            listTrackingData: sponsoredTrackingValues,
            adId,
            event
        });
    }, [adId, product.id, product.variants, sponsoredTrackingValues]);

    const handleSponsoredImpressionTracking = useCallback(async () => {
        if (sponsoredCardImpressionsUrl) {
            try {
                await get(sponsoredCardImpressionsUrl);
            } catch (error) {}
        }

        if (isSponsoredTrackingFeatureActive) {
            handlePushToTracking(SP_EVENT_PRODUCT_IMPRESSION);
        }
    }, [handlePushToTracking, isSponsoredTrackingFeatureActive, sponsoredCardImpressionsUrl]);

    useEffect(() => {
        if (observer && !observerTriggered) {
            handleSponsoredImpressionTracking();
            setObserverTriggered(true);
        }
    }, [handleSponsoredImpressionTracking, observer, observerTriggered]);

    useEffect(() => {
        setIsActiveDealsOn(activatedDeals);
    }, [activatedDeals]);

    if (!productData) {
        return null;
    }

    const { thumbnails, data: productCardData, carouselImages } = productData;

    const hasMultipleVariants = thumbnails.length > 1;
    const hasHoverEffect = hasHoverEffectProp
        && cardConfig.hasHoverEffect
        && hasMultipleVariants
        && (shouldEnableHoverOnBothWebshops || isBoozt(config));
    const hasTeaserDate = productCardData.teaserDate !== null;

    const handleSponsoredTracking = async () => {
        if (sponsoredCardClickUrl && !isSponsoredClickUrlFeatureActive) {
            try {
                await get(sponsoredCardClickUrl);
            } catch (error) {}
        }
    };

    const onClickHandler = (event) => {
        if (isSelectable) {
            event.preventDefault();
        }

        onClick(event);
        setRealListValue(realListValue, event);
        setItemsParameters(itemsParameters);
        handleBmpTracking(bmpTrackingSettings);
    };

    const onLinkClickHandler = (event) => {
        if (isSelectable) {
            event.preventDefault();
        }

        handleSponsoredTracking();
        onLinkClick(event);
        setRealListValue(realListValue, event);
        setItemsParameters(itemsParameters);
        handleBmpTracking(bmpTrackingSettings);
    };

    const onLinkContextMenuHandler = (event) => {
        onLinkContextMenu(event);
        setRealListValue(realListValue, event);
        setItemsParameters(itemsParameters);
        handleBmpTracking(bmpTrackingSettings);
    };

    const getFavoriteButton = () => {
        const { actionButtonsPosition } = cardConfig;

        if (hasToShowFavoriteButton) {
            return favoriteButton({
                favoritesTrackingValue,
                actionButtonsPosition,
                itemsParameters,
                interactionOrigin
            });
        }

        return null;
    };

    const className = classNames(classNameProp, {
        'impression-product': hasToTrackImpressions
    });

    return (
        <ProductCardContextProvider
            actionButtonsPosition={cardConfig.actionButtonsPosition}
            activeVariantId={thumbnails[0].variantId}
            cardLabels={cardStrings}
            cardType={type}
            floatPriceToRight={cardConfig.floatPriceToRight}
            hasHoverEffect={hasHoverEffect}
            imageBackground="grey"
            isDisabled={isDisabled ?? (!isInStock && !hasTeaserDate)}
            isLazy={lazyLoad}
            isSelectable={isSelectable}
            orientation={cardConfig.orientation}
            showSizes={cardConfig?.showSizes}
            showSizesOnHover={cardConfig?.showSizesOnHover}
            size={cardConfig.size}
            theme={theme}
        >
            <ProductCard
                ref={targetRef}
                actionButtons={getActionsButtons(actionButtons, cardConfig.actionButtonsPosition)}
                brandnameFieldTagname={brandnameFieldTagname}
                carouselImages={isCarouselEnabled ? carouselImages : []}
                className={className}
                customDescriptionComponent={customDescriptionComponent}
                dataAttributes={dataAttributes}
                favoriteButton={getFavoriteButton()}
                hasToShowFavoriteButtonLast={hasToShowFavoriteButtonLast}
                isSelected={isSelected}
                modelImageMode={modelImageMode && isModelImageSwitchEnabled}
                prices={productPrices}
                productData={productCardData}
                productPricesStructure={productPricesStructure}
                scrollIndicatorAnimation={withAnimation}
                thumbnails={thumbnails}
                topHeaderComponent={topHeaderComponent}
                onClick={onClickHandler}
                onLinkClick={onLinkClickHandler}
                onLinkContextMenu={onLinkContextMenuHandler}
                onMouseDown={onMouseDown}
                {...isFunction(onDisabledNotificationClick) && {
                    onDisabledNotificationClick
                }}
                {...isFunction(onLinkContextMenu) && {
                    onLinkContextMenu
                }}
                {...isFunction(onClick) && {
                    onClick
                }}
                allowVideo={allowVideo}
            />
        </ProductCardContextProvider>
    );
}

export const ProductCardStandalone = ReactOnRailsRootComponent(props => (
    <ProductCardRedesign {...props} />
), { withReduxStore: true });

export default ProductCardRedesign;
